/**
 * pd-blog.js
 * Blog collection page: tag filtering (AJAX), virtual client-side pagination,
 * multi-blog article loading, and sorting.
 *
 * ── Why JS pagination? ──
 * Shopify's Liquid `paginate` tag counts ALL blog.articles — including ones
 * we exclude by tag (featured articles). This means if we paginate by 8 and
 * 2 of the 8 fetched articles are featured, only 6 render — an inconsistent
 * grid. There is no Liquid-only way to paginate a filtered subset.
 *
 * ── Multi-blog support ──
 * Liquid renders the current blog's articles (paginated by 50) plus other
 * blogs' first 50 articles on page 1. This script then:
 *   1. Fetches remaining Shopify pages for the current blog
 *   2. Fetches remaining pages for each other blog
 *   3. Sorts the combined set chronologically (newest first)
 *   4. Virtual-paginates the full grid on the client
 */
(function () {
  'use strict';

  const parser = new DOMParser();

  const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

  // ── URL helpers ─────────────────────────────────────────────────────────
  function getPageFromUrl() {
    return parseInt(new URLSearchParams(window.location.search).get('vpage'), 10) || 1;
  }

  function setPageInUrl(page) {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete('vpage');
    } else {
      url.searchParams.set('vpage', page);
    }
    history.replaceState(null, '', url.toString());
  }

  // ── Sort utility (module-level so both toolbar and loaders can use it) ──
  function sortGrid(grid, key) {
    if (!grid) return;
    const articles = Array.from(grid.querySelectorAll('.pd-main-blog__article'));
    const promos = Array.from(grid.querySelectorAll('.pd-main-blog__promo-tile'));

    articles.sort((a, b) => {
      if (key === 'newest') return parseInt(b.dataset.sortDate, 10) - parseInt(a.dataset.sortDate, 10);
      if (key === 'oldest') return parseInt(a.dataset.sortDate, 10) - parseInt(b.dataset.sortDate, 10);
      if (key === 'az') return (a.dataset.sortTitle || '').localeCompare(b.dataset.sortTitle || '');
      if (key === 'za') return (b.dataset.sortTitle || '').localeCompare(a.dataset.sortTitle || '');
      return 0;
    });

    const frag = document.createDocumentFragment();
    [...articles, ...promos].forEach(el => frag.appendChild(el));
    grid.appendChild(frag);
  }

  // Read the active sort key from the DOM
  function getActiveSortKey(container) {
    const active = container.querySelector('.pd-main-blog__sort-option--active');
    return active?.dataset.sort ?? 'newest';
  }

  // ── Virtual Pagination ──────────────────────────────────────────────────
  function initVirtualPagination(container, startPage) {
    const grid = container.querySelector('[data-blog-grid]');
    if (!grid) return;

    const perPage = parseInt(grid.dataset.perPage, 10) || 9;
    const allChildren = Array.from(grid.querySelectorAll('.pd-main-blog__grid-item'));
    const articles = [];
    const promos = [];

    allChildren.forEach(item => {
      if (item.classList.contains('pd-main-blog__promo-tile')) {
        promos.push({ el: item, after: parseInt(item.dataset.promoAfter, 10) || 0 });
      } else {
        articles.push(item);
      }
    });

    const articlesPerPage = Math.max(1, perPage - promos.length);
    const totalPages = Math.max(1, Math.ceil(articles.length / articlesPerPage));

    container.querySelector('[data-virtual-pagination]')?.remove();

    if (totalPages <= 1 && articles.length + promos.length <= perPage && promos.length === 0) {
      allChildren.forEach(item => { item.style.display = ''; });
      setPageInUrl(1);
      container._vpState = { showPage: () => {}, totalPages: 1 };
      return;
    }

    let navEl = null;

    function showPage(page) {
      page = Math.min(Math.max(1, page), totalPages);

      allChildren.forEach(item => { item.style.display = 'none'; });

      const startIdx = (page - 1) * articlesPerPage;
      const pageArticles = articles.slice(startIdx, Math.min(startIdx + articlesPerPage, articles.length));

      pageArticles.forEach(item => { item.style.display = ''; });

      const sortedPromos = promos.slice().sort((a, b) => b.after - a.after);
      sortedPromos.forEach(promo => {
        promo.el.style.display = '';
        const insertAfter = Math.min(promo.after, pageArticles.length);
        if (insertAfter <= 0) {
          grid.insertBefore(promo.el, pageArticles[0] || null);
        } else {
          const ref = pageArticles[insertAfter - 1];
          grid.insertBefore(promo.el, ref.nextSibling || null);
        }
      });

      setPageInUrl(page);
      renderNav(page);
    }

    function renderNav(current) {
      if (!navEl || !navEl.isConnected) {
        navEl = document.createElement('nav');
        navEl.className = 'pd-blog-pagination';
        navEl.setAttribute('data-virtual-pagination', '');
        navEl.setAttribute('aria-label', 'Blog pagination');
        grid.parentNode.insertBefore(navEl, grid.nextSibling);
      }

      if (totalPages <= 1) { navEl.innerHTML = ''; return; }

      let html = current > 1
        ? `<a class="pd-blog-pagination__arrow" href="#" data-virtual-page="${current - 1}" aria-label="Previous page">Prev</a>`
        : '<span class="pd-blog-pagination__arrow pd-blog-pagination__arrow--disabled" aria-disabled="true">Prev</span>';

      buildPageList(current, totalPages).forEach(p => {
        if (p === '...') {
          html += '<span class="pd-blog-pagination__page" aria-hidden="true">...</span>';
        } else if (p === current) {
          html += `<span class="pd-blog-pagination__page pd-blog-pagination__page--active" aria-current="page">${p}</span>`;
        } else {
          html += `<a class="pd-blog-pagination__page" href="#" data-virtual-page="${p}" aria-label="Page ${p}">${p}</a>`;
        }
      });

      html += current < totalPages
        ? `<a class="pd-blog-pagination__arrow" href="#" data-virtual-page="${current + 1}" aria-label="Next page">Next</a>`
        : '<span class="pd-blog-pagination__arrow pd-blog-pagination__arrow--disabled" aria-disabled="true">Next</span>';

      navEl.innerHTML = html;
    }

    function buildPageList(current, total) {
      const isMobile = window.innerWidth < 990;
      const threshold = isMobile ? 5 : 7;
      const wing = isMobile ? 0 : 1;
      if (total <= threshold) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      let showStart = Math.max(2, current - wing);
      let showEnd = Math.min(total - 1, current + wing);
      if (showEnd < showStart) {
        if (current <= 2) showEnd = showStart;
        else showStart = showEnd;
      }
      if (showStart > 2) pages.push('...');
      for (let j = showStart; j <= showEnd; j++) pages.push(j);
      if (showEnd < total - 1) pages.push('...');
      pages.push(total);
      return pages;
    }

    container._vpState = { showPage, totalPages };

    if (!container._vpListenerAdded) {
      container._vpListenerAdded = true;
      container.addEventListener('click', e => {
        const btn = e.target.closest('[data-virtual-page]');
        if (!btn) return;
        e.preventDefault();
        const state = container._vpState;
        if (!state) return;
        const page = parseInt(btn.dataset.virtualPage, 10);
        if (isNaN(page) || page < 1 || page > state.totalPages) return;
        state.showPage(page);
        const rect = container.getBoundingClientRect();
        window.scrollTo({
          top: window.scrollY + rect.top - (window.innerWidth >= 990 ? 300 : 230),
          behavior: scrollBehavior
        });
      });
    }

    const page = startPage != null ? startPage : getPageFromUrl();
    showPage(Math.min(Math.max(1, page), totalPages));
  }

  // ── Featured Article Exclusion ──────────────────────────────────────────
  function excludeFeaturedFromGrid(container) {
    const src = document.querySelector('[data-featured-ids]');
    if (!src) return;
    const ids = src.dataset.featuredIds.split(',').map(id => id.trim()).filter(Boolean);
    if (!ids.length) return;
    const grid = container.querySelector('[data-blog-grid]');
    if (!grid) return;
    ids.forEach(id => {
      const el = grid.querySelector('[data-article-id="' + id + '"]');
      if (el) el.remove();
    });
    const counter = container.querySelector('[data-article-count]');
    if (counter) {
      counter.textContent = grid.querySelectorAll('[data-article-id]').length + ' Articles';
    }
  }

  // ── Update article counter ──────────────────────────────────────────────
  function updateArticleCount(container) {
    const grid = container.querySelector('[data-blog-grid]');
    const counter = container.querySelector('[data-article-count]');
    if (grid && counter) {
      counter.textContent = grid.querySelectorAll('[data-article-id]').length + ' Articles';
    }
  }

  // ── Multi-page Loader (current blog) ───────────────────────────────────
  //
  // Fetches Shopify paginate pages 2+ for the CURRENT blog and appends
  // their articles to the grid. Sequential to preserve order.
  //
  function loadRemainingShopifyPages(container, sectionId) {
    const grid = container.querySelector('[data-blog-grid]');
    if (!grid) return;

    const totalShopifyPages = parseInt(grid.dataset.shopifyPages, 10) || 1;
    if (totalShopifyPages <= 1) return;

    const baseUrl = new URL(window.location.href);
    baseUrl.searchParams.delete('page');
    baseUrl.searchParams.delete('vpage');
    baseUrl.searchParams.delete('sections');

    (async () => {
      for (let shopifyPage = 2; shopifyPage <= totalShopifyPages; shopifyPage++) {
        if (!grid.isConnected) break;

        try {
          const url = new URL(baseUrl.href);
          url.searchParams.set('page', shopifyPage);
          url.searchParams.set('sections', sectionId);

          const res = await fetch(url.toString());
          const data = await res.json();
          const sectionHtml = data[sectionId];
          if (!sectionHtml) continue;

          const newGrid = parser
            .parseFromString(sectionHtml, 'text/html')
            .querySelector('[data-blog-grid]');
          if (!newGrid) continue;

          const newArticles = newGrid.querySelectorAll('.pd-main-blog__article');
          if (!newArticles.length) continue;

          const frag = document.createDocumentFragment();
          newArticles.forEach(el => frag.appendChild(el));
          grid.appendChild(frag);
          excludeFeaturedFromGrid(container);

          initVirtualPagination(container, getPageFromUrl());
        } catch {
          break;
        }
      }
    })();
  }

  // ── Multi-blog Loader ──────────────────────────────────────────────────
  //
  // For each "other" blog listed in data-other-blogs, fetches Shopify
  // pages 2+ (page 1 was rendered inline by Liquid). Extracts only
  // articles from that specific blog via data-blog-handle to avoid
  // duplicates (the section renders articles from ALL blogs on page 1).
  //
  // After all extra articles are loaded, sorts the full grid by the
  // currently active sort key and re-inits virtual pagination.
  //
  function loadOtherBlogsPages(container, sectionId) {
    const grid = container.querySelector('[data-blog-grid]');
    if (!grid) return;

    let otherBlogs;
    try { otherBlogs = JSON.parse(grid.dataset.otherBlogs || '[]'); }
    catch { return; }
    if (!otherBlogs.length) return;

    // Detect current tag filter from URL path (e.g. /tagged/news)
    const tagMatch = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
    const tagSegment = tagMatch ? '/tagged/' + tagMatch[1] : '';

    (async () => {
      let added = false;

      for (const blogMeta of otherBlogs) {
        if (!grid.isConnected) break;
        if (blogMeta.p <= 1) continue; // no extra pages

        for (let page = 2; page <= blogMeta.p; page++) {
          if (!grid.isConnected) break;

          try {
            const url = new URL('/blogs/' + blogMeta.h + tagSegment, window.location.origin);
            url.searchParams.set('page', page);
            url.searchParams.set('sections', sectionId);

            const res = await fetch(url.toString());
            const data = await res.json();
            const sectionHtml = data[sectionId];
            if (!sectionHtml) continue;

            const doc = parser.parseFromString(sectionHtml, 'text/html');
            const newGrid = doc.querySelector('[data-blog-grid]');
            if (!newGrid) continue;

            // Only take articles — no promos, no duplicates from other blogs'
            // page-1 render. On page 2+ the Liquid skips other-blog articles,
            // so all returned articles belong to this blog.
            const newArticles = newGrid.querySelectorAll('.pd-main-blog__article');
            if (!newArticles.length) break; // no more articles for this blog

            const frag = document.createDocumentFragment();
            newArticles.forEach(el => frag.appendChild(el));
            grid.appendChild(frag);
            added = true;

            excludeFeaturedFromGrid(container);
            initVirtualPagination(container, getPageFromUrl());
          } catch {
            break; // stop this blog on error, try next
          }
        }
      }

      // Re-sort the combined grid so articles from all blogs interleave
      // chronologically (or by whatever sort is active).
      if (added) {
        const key = getActiveSortKey(container);
        sortGrid(grid, key);
        updateArticleCount(container);
        initVirtualPagination(container, 1);
      }
    })();
  }

  // ── Toolbar: Sort + Filter Panel ────────────────────────────────────────
  let _closeDropdowns = null;

  function initToolbar(container) {
    const toolbar = container.querySelector('[data-blog-toolbar]');
    if (!toolbar) return;

    const grid = container.querySelector('[data-blog-grid]');
    const sortBtn = toolbar.querySelector('[data-sort-toggle]');
    const sortDropdown = toolbar.querySelector('[data-sort-dropdown]');
    const sortLabel = toolbar.querySelector('[data-sort-label]');
    const filterBtn = toolbar.querySelector('[data-filter-toggle]');
    const filterPanel = container.querySelector('[data-filter-panel]');

    const activeOption = sortDropdown?.querySelector('[aria-selected="true"]');
    let currentSort = activeOption?.dataset.sort ?? 'newest';

    const closeSort = () => {
      sortBtn?.setAttribute('aria-expanded', 'false');
      if (sortDropdown) sortDropdown.hidden = true;
    };

    const closeFilter = () => {
      filterBtn?.setAttribute('aria-expanded', 'false');
      if (filterPanel) filterPanel.hidden = true;
    };

    _closeDropdowns = () => { closeSort(); closeFilter(); };

    if (sortBtn && sortDropdown) {
      sortBtn.addEventListener('click', e => {
        e.stopPropagation();
        const isOpen = sortBtn.getAttribute('aria-expanded') === 'true';
        sortBtn.setAttribute('aria-expanded', String(!isOpen));
        sortDropdown.hidden = isOpen;
        if (!isOpen) {
          closeFilter();
          sortDropdown.style.right = '0';
          sortDropdown.style.left = '';
          if (sortDropdown.getBoundingClientRect().left < 0) {
            sortDropdown.style.right = '';
            sortDropdown.style.left = '0';
          }
        }
      });

      sortDropdown.addEventListener('click', e => {
        const option = e.target.closest('[data-sort]');
        if (!option) return;
        const sortKey = option.dataset.sort;
        if (sortKey === currentSort) { closeSort(); return; }

        currentSort = sortKey;
        sortDropdown.querySelectorAll('[data-sort]').forEach(o => {
          o.classList.toggle('pd-main-blog__sort-option--active', o.dataset.sort === sortKey);
          o.setAttribute('aria-selected', String(o.dataset.sort === sortKey));
        });
        if (sortLabel) sortLabel.textContent = option.textContent.trim();
        closeSort();

        sortGrid(grid, sortKey);
        initVirtualPagination(container, 1);
      });
    }

    if (filterBtn && filterPanel) {
      filterBtn.addEventListener('click', e => {
        e.stopPropagation();
        const isOpen = filterBtn.getAttribute('aria-expanded') === 'true';
        filterBtn.setAttribute('aria-expanded', String(!isOpen));
        filterPanel.hidden = isOpen;
        if (!isOpen) closeSort();
      });
    }
  }

  // ── AJAX Tag Filter ──────────────────────────────────────────────────────
  function initTagFilter(sectionId) {
    let activeController = null;

    function fetchSection(url, mainBlogEl, onSuccess) {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      mainBlogEl.setAttribute('aria-busy', 'true');

      const fetchUrl = new URL(url, window.location.href);
      fetchUrl.searchParams.set('sections', sectionId);

      fetch(fetchUrl.toString(), { signal: activeController.signal })
        .then(res => res.json())
        .then(data => {
          const sectionHtml = data[sectionId];
          if (!sectionHtml) { window.location.href = url; return; }

          const newContent = parser
            .parseFromString(sectionHtml, 'text/html')
            .querySelector('[data-main-blog]');

          if (newContent) onSuccess(newContent);
        })
        .catch(err => {
          if (err.name !== 'AbortError') window.location.href = url;
        })
        .finally(() => {
          mainBlogEl.removeAttribute('aria-busy');
        });
    }

    document.addEventListener('click', e => {
      const item = e.target.closest('[data-blog-filter-item]');
      if (!item) return;

      const targetUrl = item.getAttribute('href');
      if (!targetUrl) return;

      const mainBlogEl = document.querySelector('[data-main-blog]');
      if (!mainBlogEl) return;

      e.preventDefault();

      document.querySelectorAll('[data-blog-filter-item]').forEach(fi => {
        const isActive = fi.getAttribute('href') === targetUrl;
        fi.classList.toggle('pd-blog-filter__item--active', isActive);
        fi.classList.toggle('pd-main-blog__filter-tag--active', isActive);
        fi.setAttribute('aria-selected', String(isActive));
        if (isActive) fi.setAttribute('aria-current', 'true');
        else fi.removeAttribute('aria-current');
      });

      item.scrollIntoView({ behavior: scrollBehavior, block: 'nearest', inline: 'center' });

      fetchSection(targetUrl, mainBlogEl, newContent => {
        mainBlogEl.innerHTML = newContent.innerHTML;
        excludeFeaturedFromGrid(mainBlogEl);
        history.pushState({ path: targetUrl }, '', targetUrl);
        // Sort combined articles by active key before paginating
        const grid = mainBlogEl.querySelector('[data-blog-grid]');
        if (grid) sortGrid(grid, getActiveSortKey(mainBlogEl));
        initVirtualPagination(mainBlogEl, 1);
        initToolbar(mainBlogEl);
        updateArticleCount(mainBlogEl);
        loadRemainingShopifyPages(mainBlogEl, sectionId);
        loadOtherBlogsPages(mainBlogEl, sectionId);
        const rect = mainBlogEl.getBoundingClientRect();
        window.scrollTo({
          top: window.scrollY + rect.top - (window.innerWidth >= 990 ? 300 : 230),
          behavior: scrollBehavior
        });
      });
    });

    window.addEventListener('popstate', () => {
      const currentPath = window.location.pathname;

      document.querySelectorAll('[data-blog-filter-item]').forEach(item => {
        const href = item.getAttribute('href') || '';
        const isActive = href === currentPath || href.replace(/\/$/, '') === currentPath.replace(/\/$/, '');
        item.classList.toggle('pd-blog-filter__item--active', isActive);
        item.classList.toggle('pd-main-blog__filter-tag--active', isActive);
        if (isActive) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      const mainBlogEl = document.querySelector('[data-main-blog]');
      if (!mainBlogEl) return;

      fetchSection(window.location.href, mainBlogEl, newContent => {
        mainBlogEl.innerHTML = newContent.innerHTML;
        excludeFeaturedFromGrid(mainBlogEl);
        const grid = mainBlogEl.querySelector('[data-blog-grid]');
        if (grid) sortGrid(grid, getActiveSortKey(mainBlogEl));
        const page = getPageFromUrl();
        initVirtualPagination(mainBlogEl, page);
        initToolbar(mainBlogEl);
        updateArticleCount(mainBlogEl);
        loadRemainingShopifyPages(mainBlogEl, sectionId);
        loadOtherBlogsPages(mainBlogEl, sectionId);
      });
    });
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────
  const mainBlogEl = document.querySelector('[data-main-blog]');
  const sectionId = mainBlogEl?.dataset.sectionId ?? 'pd-main-blog';

  if (mainBlogEl) {
    excludeFeaturedFromGrid(mainBlogEl);
    // Sort combined articles from all blogs by newest before first pagination
    const grid = mainBlogEl.querySelector('[data-blog-grid]');
    if (grid) sortGrid(grid, 'newest');
    initVirtualPagination(mainBlogEl);
    initToolbar(mainBlogEl);
    updateArticleCount(mainBlogEl);
    loadRemainingShopifyPages(mainBlogEl, sectionId);
    loadOtherBlogsPages(mainBlogEl, sectionId);
  }

  initTagFilter(sectionId);

  document.addEventListener('click', () => { _closeDropdowns?.(); });
})();
